(function () {
/**
 * Format a cents integer as a dollar string, e.g. 1999 → "$19.99".
 * @param {number} cents
 * @returns {string}
 */
function formatMoney(cents) {
    const amount = cents / 100;
    return amount % 1 === 0
        ? `$${amount}`
        : `$${amount.toFixed(2)}`;
}

/**
 * Show loading state on element
 * @param {HTMLElement} element - Element to show loading on
 */
function addWorking(element) {
    element.classList.add('working');
}

/**
 * Hide loading state on element
 * @param {HTMLElement} element - Element to hide loading on
 */
function removeWorking(element) {
    element.classList.remove('working');
}

/**
 * Shipping progress helpers (applyShippingProgress, etc.) live in theme.js,
 * which always loads first. Do not redeclare `const settings` / related
 * bindings here — classic scripts share one global lexical scope.
 */

/**
 * Product Form
 *
 * - Syncs option radio changes to the hidden select[name="id"] (variant ID).
 * - Disables the submit button when the selected variant is unavailable.
 * - AJAX-submits the form and fires the same cart events as quick-add.js:
 *     cart:count:updated  { count, total }  → badge + shipping progress
 *     cart:item:added                        → cart panel opens + refreshes
 * - Reads Skio's [name="selling_plan"] for subscription add-to-cart + shipping preview
 */

const form = document.querySelector('.product__form form');
if (!form) throw new Error('Product form not found');

const variantDataEl = document.getElementById('product-variants-data');
if (!variantDataEl) throw new Error('product-variants-data element not found');

const variants = JSON.parse(variantDataEl.textContent);
const optionContainer = form.querySelector('.product__options');
const variantSelect = form.querySelector('select[name="id"]');
const submitButton = form.querySelector('[type="submit"]');
const purchaseOptions = form.querySelector('purchase-options');

// ---------------------------------------------------------------------------
// Cart total tracking — kept fresh so previews are always based on reality
// ---------------------------------------------------------------------------

let currentCartTotal = 0;
/** Skio writes the hidden input *after* this event; keep the event as source of truth. */
let skioHeard = false;
let subscriptionSelected = false;

function sellingPlanInput() {
    return form.querySelector('[name="selling_plan"]')
        || document.querySelector('input[name="selling_plan"]');
}

function isSubscriptionSelected() {
    if (skioHeard) return subscriptionSelected;
    return !!(sellingPlanInput()?.value?.trim());
}

// Seed from the live cart on page load, then refresh the PDP preview
fetch('/cart.js')
    .then(r => r.json())
    .then(cart => {
        currentCartTotal = cart.total_price;
        previewShippingProgress();
    })
    .catch(() => { });

// Stay in sync after any cart mutation elsewhere on the page
document.addEventListener('cart:count:updated', e => {
    currentCartTotal = e.detail.total;
    // Re-apply PDP preview so global cart shipping updates don't leave a stale bar
    previewShippingProgress();
});

// ---------------------------------------------------------------------------
// Variant resolution — mirrors getSelectedVariant() in quick-add.js
// ---------------------------------------------------------------------------

/**
 * Find the variant matching the currently checked option radios.
 * @returns {Object|null} Shopify variant object, or null if no match
 */
function getSelectedVariant() {
    const fieldsets = [...optionContainer.querySelectorAll('[data-option-position]')];
    // if we don't have fieldsets, then this product has no options and only one variant — return it
    if (fieldsets.length === 0) return variants[0] || null;
    const selectedOptions = fieldsets.map(fieldset => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
    });

    if (selectedOptions.some(o => o === null)) return null;

    return variants.find(variant =>
        variant.options.every((option, index) => option === selectedOptions[index])
    ) || null;
}

// ---------------------------------------------------------------------------
// Update select + submit state after option change
// ---------------------------------------------------------------------------

/** Sync the hidden variant select and submit button to the currently checked options. */
function updateVariantState() {
    const variant = getSelectedVariant();

    if (variant) {
        variantSelect.value = variant.id;
        submitButton.disabled = !variant.available;
        submitButton.value = variant.available ? 'Add to cart' : 'Sold out';
    } else {
        variantSelect.value = '';
        submitButton.disabled = true;
        submitButton.value = 'Unavailable';
    }

    // Notify listeners (e.g. Skio) that the variant select changed
    variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('variantChanged', {
        detail: { variantId: variant ? variant.id : null },
    }));

    // Stay AI watched URL/popstate; keep for compatibility if purchase-options is present
    if (purchaseOptions) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant ? variant.id : '');
        window.history.replaceState({}, '', url);
        // replaceState doesn't fire popstate — dispatch it manually
        window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
    }

    previewShippingProgress();
}

// ---------------------------------------------------------------------------
// Preview shipping progress — scoped to the form's own progress bar only,
// so the cart panel bar is never affected.
// ---------------------------------------------------------------------------

/** Preview free shipping progress based on the current selection and live cart total. */
function previewShippingProgress() {
    const progressEl = form.querySelector('.shipping-progress');
    if (!progressEl) return;

    const subOn = isSubscriptionSelected();
    progressEl.classList.toggle('is-sub', subOn);

    // Subscription → free shipping (v1 unlocked copy via applyShippingProgress)
    if (subOn) {
        const freeThreshold = Number(progressEl.dataset.threshold) || 0;
        applyShippingProgress(progressEl, freeThreshold);
        return;
    }

    const variant = getSelectedVariant();
    if (!variant) return;

    const quantity = parseInt(form.querySelector('.quantity-input__value')?.value || 1, 10);
    const previewTotal = currentCartTotal + variant.price * quantity;
    applyShippingProgress(progressEl, previewTotal, true);
}

// ---------------------------------------------------------------------------
// Add to cart — mirrors addToCart() in quick-add.js
// ---------------------------------------------------------------------------

/**
 * POST the selected variant to the Cart API and fire cart update events.
 * @param {number|string} variantId - Shopify variant ID
 */
async function addToCart(variantId) {
    addWorking(form);
    const requestBody = {
        id: variantId,
        quantity: parseInt(form.querySelector('[name="quantity"]')?.value || 1)
    };
    const sellingPlanInput = form.querySelector('[name="selling_plan"]');
    if (sellingPlanInput?.value) {
        requestBody.selling_plan = sellingPlanInput.value;
    }
    try {
        const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
        });
        if (!res.ok) throw new Error('Cart add failed');
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        document.dispatchEvent(new CustomEvent('cart:count:updated', {
            detail: { count: cart.item_count, total: cart.total_price }
        }));
        document.dispatchEvent(new CustomEvent('cart:item:added'));
    } finally {
        removeWorking(form);
    }
}

// ---------------------------------------------------------------------------
// Event listeners
// ---------------------------------------------------------------------------

form.addEventListener('click', e => {
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (!qtyBtn) return;
    e.preventDefault();
    const delta = parseInt(qtyBtn.dataset.qtyChange, 10);
    const qtyInput = form.querySelector('.quantity-input__value');
    if (!qtyInput) return;
    const current = parseInt(qtyInput.value, 10) || 1;
    qtyInput.value = Math.max(1, current + delta);
    previewShippingProgress();
});

// Live qty typing
form.addEventListener('input', e => {
    if (e.target.matches('.quantity-input__value')) previewShippingProgress();
});

// Variant radios, quantity
form.addEventListener('change', previewShippingProgress);

// Skio fires this before updateForm() writes the hidden input — use event detail
document.addEventListener('skio::update-selling-plan', e => {
    skioHeard = true;
    subscriptionSelected = !!e.detail?.sellingPlan?.id;
    previewShippingProgress();
});

optionContainer.addEventListener('change', updateVariantState);

form.addEventListener('submit', async e => {
    e.preventDefault();
    const variantId = variantSelect.value;
    if (!variantId) return;
    await addToCart(variantId);
});

// Set initial state on load (Liquid pre-selects checked radios, so this
// ensures the select and button reflect the server-rendered selection)
updateVariantState();
previewShippingProgress();
})();
